import React, { useCallback, useEffect, useState } from "react";
import useMetadata from "app/hooks/useMetadata";
import { useNavigate } from "react-router-dom";
import useGetProgrammingContentsForNavigation from "app/hooks/useGetProgrammingContentsForNavigation";
import Filters from "./Filters";
import ContentTable from "./ContentTable";
import { Pagination } from "components/Pagination";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import ContentHeader from "components/ContentHeader/ContentHeader";
import { ROUTES } from "app/utils/constants/routeConstants";
import { useSearchParams } from "react-router-dom";

const AdminProgrammingContentList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { metadata } = useMetadata();

  // Initialize state from searchParams
  const initialRowsPerPage = Number(searchParams.get("size")) || 25;
  const initialPage = Number(searchParams.get("page")) || 1;
  const initialLanguageFilter = searchParams.get("language") || "";

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [languageFilter, setLanguageFilter] = useState(initialLanguageFilter);
  const [searchQuery, setSearchQuery] = useState("");

  const languages = (metadata?.programmingLanguages || []).map(
    ({ key, value }) => ({
      key,
      value,
    })
  );

  const statusOptions = (metadata?.problemStates || []).map(
    ({ key, value }) => ({
      key,
      value,
    })
  );

  const { contents, totalCount, error, loading } =
    useGetProgrammingContentsForNavigation(
      currentPage,
      rowsPerPage,
      languageFilter
    );

  // Sync search params with state
  useEffect(() => {
    const params = {
      size: rowsPerPage.toString(),
      page: currentPage.toString(),
    };
    if (languageFilter) {
      params.language = languageFilter;
    }
    setSearchParams(params);
  }, [rowsPerPage, currentPage, languageFilter, setSearchParams]);

  // Filter contents based on search query
  const filteredContents = contents.filter((content) =>
    (content.title || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLanguageFilterChange = (value) => {
    setLanguageFilter(value);
    setCurrentPage(1);
  };

  const handleSearchQueryChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setCurrentPage(1);
  };

  const handleAddContent = useCallback(() => {
    navigate(`${ROUTES.ADMIN_PROGRAMMING_CONTENT_ADD}`);
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
    setTimeout(() => {
      if (window.location.pathname) {
        navigate(ROUTES.USER_HOME);
      }
    }, 100);
  }, [navigate]);

  const handleAction = useCallback(
    (action) => {
      switch (action.id) {
        case "back":
          handleBack();
          break;
        case "add":
          handleAddContent();
          break;
        default:
          console.log("Unknown action:", action);
      }
    },
    [handleAddContent, handleBack]
  );

  return (
    <JumboCardQuick
      title={
        <ContentHeader
          isAdmin={true}
          onAction={handleAction}
          allowedActions={["add", "back"]}
          showLeftTabs={true}
        />
      }
      headerSx={{
        borderBottom: 1,
        borderColor: "divider",
        "& .MuiCardHeader-action": { my: -0.75 },
      }}
      wrapperSx={{ p: 0 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "& .MuiCardContent-root:last-child": {
          paddingBottom: 0,
        },
        "& .MuiCardContent-root": {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
        },
      }}
    >
      <Filters
        languages={languages}
        languageFilter={languageFilter}
        onLanguageFilterChange={handleLanguageFilterChange}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <ContentTable
        contents={filteredContents}
        error={error}
        languages={languages}
        statusOptions={statusOptions}
        navigate={navigate}
        loading={loading}
        currentPage={currentPage - 1}
        rowsPerPage={rowsPerPage}
      />

      <Pagination
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        page={currentPage - 1}
        onPageChange={handleChangePage}
      />
    </JumboCardQuick>
  );
};

export default AdminProgrammingContentList;
